import { ROSELoader } from "./rose";

/**
 * LTB (Lookup Table) Loader
 * Loads string lookup tables with row/column structure
 */
export class LookupTable {
  rh: any;
  columnCount: number = 0;
  rowCount: number = 0;
  data: any[][] = [];

  constructor(rh: any) {
    this.rh = rh;
  }

  /**
   * Get a string from the lookup table
   * @param {number} row - Row index (0-based)
   * @param {number} column - Column index (0-based)
   * @returns {string} The string at the specified position
   */
  getString(row: number, column: number): string {
    if (
        row >= this.rowCount ||
        column >= this.columnCount ||
        row < 0 ||
        column < 0
    ) {
        return "";
    }

    const cell = this.data[row][column];
    if (!cell || cell.length === 0) {
        return "";
    }

    // LTB uses UTF-16LE encoding (USTR = Unicode string)
    // The length field represents character count, but UTF-16LE uses 2 bytes per character
    const byteLength = cell.length * 2;

    // Check if offset is valid
    if (cell.offset >= this.rh.buffer.byteLength) {
        console.error(
            `getString(${row}, ${column}): offset ${cell.offset} is beyond file end (${this.rh.buffer.byteLength})`
        );
        return "";
    }

    // Check if we have enough bytes
    if (cell.offset + byteLength > this.rh.buffer.byteLength) {
        console.error(
            `getString(${row}, ${column}): need ${byteLength} bytes but only ${
                this.rh.buffer.byteLength - cell.offset
            } available`
        );
        return "";
    }

    this.rh.seek(cell.offset);
    const bytes = this.rh.readUint8Array(byteLength);

    // Convert UTF-16LE bytes to string
    let str = "";
    try {
        // Use TextDecoder for proper UTF-16LE decoding
        const decoder = new TextDecoder("utf-16le");
        str = decoder.decode(bytes);

        // Remove null terminators
        str = str.replace(/\0+$/g, "");
    } catch (e) {
        console.error(`Failed to decode UTF-16LE string:`, e);
        // Fallback to manual UTF-16LE conversion
        for (let i = 0; i < bytes.length; i += 2) {
            if (i + 1 < bytes.length) {
                const charCode = bytes[i] | (bytes[i + 1] << 8);
                if (charCode === 0) break; // Stop at null terminator
                str += String.fromCharCode(charCode);
            }
        }
    }
    return str;
  }

  /**
   * Get all strings in a row
   * @param {number} row - Row index (0-based)
   * @returns {Array<string>} Array of strings in the row
   */
  getRow(row: number): string[] {
    const result = [];
    for (let col = 0; col < this.columnCount; col++) {
        result.push(this.getString(row, col));
    }
    return result;
  }

  /**
   * Load an LTB file
   * @param {string} path - Path to the LTB file
   * @param {Function} callback - Callback function
   */
  static parse(rh: any): LookupTable {
    const data = new LookupTable(rh);
    data.columnCount = rh.readUint32();
    data.rowCount = rh.readUint32();
    for (let row = 0; row < data.rowCount; row++) {
      data.data[row] = [];
      for (let col = 0; col < data.columnCount; col++) {
        const offset = rh.readUint32();
        const length = rh.readUint16();
        data.data[row][col] = { offset, length };
      }
    }
    return data;
  }

  static load(path: any, callback: any) {
    ROSELoader.load(path, function (rh: any) {
        callback(LookupTable.parse(rh));
    });
  }
}
